import React, { useState, useEffect, useRef } from 'react';
import { api, API_BASE, logHttpError } from '../services/api';
import { useNavigate } from 'react-router-dom';
import MediaViewer from '../components/MediaViewer';
import type { MediaItem } from '../components/MediaViewer';
import CreateGroupModal from '../components/CreateGroupModal';
import ForwardModal from '../components/ForwardModal';
import MessageContextMenu from '../components/MessageContextMenu';
import ReactionPicker from '../components/ReactionPicker';
import ChatMediaPicker, { type PickerSticker, type PickerTab, type StickerGroup } from '../components/ChatMediaPicker';
import { useCall } from '../calls/CallProvider';

interface Conversation {
  user_id: number;
  username: string;
  avatar_url: string | null;
  last_message: string;
  last_message_time: string;
  unread_count: number;
  is_my_last_message: boolean;
}

interface Message {
  id: number;
  sender_id: number;
  receiver_id: number;
  content: string;
  file_url: string | null;
  file_type: string | null;
  sticker_id: number | null;       // <-- ДОБАВЛЕНО
  sticker_url: string | null;       // <-- ДОБАВЛЕНО
  created_at: string;
  is_read: boolean;
  sender_username: string;
  sender_avatar_url: string | null;
  receiver_username: string | null;
  receiver_avatar_url: string | null;
  forwarded_from_id: number | null;
  forwarded_from_user: number | null;
  forwarded_at: string | null;
  forwarded_from_username: string | null;
  forwarded_original_created_at: string | null;
  message_kind?: 'text' | 'call';
  call_id?: string | null;
  call_type?: 'audio' | 'video' | null;
  call_state?: string | null;
  call_connected_at?: string | null;
  call_ended_at?: string | null;
  call_ended_reason?: string | null;
}

interface SearchResult {
  id: number;
  username: string;
  email: string;
  avatar_url: string | null;
}

// Популярные эмодзи для быстрой вставки
const QUICK_EMOJIS = ['😀', '😂', '🥰', '😎', '🤔', '👍', '❤️', '🔥', '🎉', '🦈', '💬', '✅'];

// Компонент мини-профиля
const MiniProfilePopup: React.FC<{
  userId: number;
  onClose: () => void;
}> = ({ userId, onClose }) => {
  const [user, setUser] = useState<any>(null);
  const [stats, setStats] = useState({ posts_count: 0, likes_count: 0 });
  const [privacy, setPrivacy] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userRes = await api.get(`/auth/user/${userId}`);
        setUser(userRes.data);
        const statsRes = await api.get(`/profile/stats/${userId}`);
        setStats(statsRes.data);
        try { const privRes = await api.get(`/profile/privacy/${userId}`); setPrivacy(privRes.data); } catch {}
      } catch (err) {
        logHttpError("Ошибка загрузки данных", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();

    const handleClickOutside = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userId, onClose]);

  if (loading || !user) return null;

  const avatarSrc = (user.avatar_url && !privacy.hide_avatar)
    ? `${API_BASE}${user.avatar_url}`
    : `https://ui-avatars.com/api/?name=${encodeURIComponent(user.username)}&background=random&color=fff`;

  const clanNames: Record<number, string> = {
    1: 'Акула 🦈', 2: 'Слон 🐘', 3: 'Горилла 🦍', 4: 'Волк 🐺'
  };

  return (
    <div ref={popupRef} className="absolute top-full left-0 mt-2 w-80 bg-[#1a1d24] border border-gray-700 rounded-3xl shadow-2xl z-[9999] overflow-hidden animate-fade-in">
      {user.banner_url ? (
        <div className="relative h-24">
          <img src={`${API_BASE}${user.banner_url}`} alt="Banner" className="w-full h-24 object-cover" />
          <button onClick={onClose} className="absolute top-3 right-3 text-white/70 hover:text-white bg-black/20 hover:bg-black/40 rounded-full w-8 h-8 flex items-center justify-center transition">✕</button>
        </div>
      ) : (
        <div className="h-24 bg-gradient-to-r from-blue-600 to-emerald-600 relative flex items-center justify-center">
          <button onClick={onClose} className="absolute top-3 right-3 text-white/70 hover:text-white bg-black/20 hover:bg-black/40 rounded-full w-8 h-8 flex items-center justify-center transition">✕</button>
        </div>
      )}

      <div className="relative px-6 -mt-14 mb-4 flex justify-center">
        <img
          src={avatarSrc}
          alt={user.username}
          className="w-28 h-28 rounded-full border-4 border-[#1a1d24] object-cover bg-[#0f1115] shadow-xl"
        />
      </div>

      <div className="px-6 pb-6 text-center">
        <h3 className="text-2xl font-black text-white mb-1">{user.username}</h3>
        {!privacy.hide_full_name && <p className="text-sm text-gray-400 mb-4 font-medium">{user.full_name}</p>}

        <div className="grid grid-cols-3 gap-2 mb-6 bg-[#0f1115]/50 p-3 rounded-2xl border border-gray-800">
          <div className="text-center">
            <div className="text-lg font-bold text-white">{stats.posts_count}</div>
            <div className="text-[10px] text-gray-500 uppercase tracking-wider">Постов</div>
          </div>
          <div className="text-center border-x border-gray-800">
            <div className="text-lg font-bold text-white">{stats.likes_count}</div>
            <div className="text-[10px] text-gray-500 uppercase tracking-wider">Лайков</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-emerald-400">{user.mood_score}</div>
            <div className="text-[10px] text-gray-500 uppercase tracking-wider">Рейтинг</div>
          </div>
        </div>

        <div className="space-y-2 mb-6 text-left bg-[#0f1115]/30 p-4 rounded-xl">
           {!privacy.hide_email && <p className="text-xs text-gray-400 flex items-center gap-2">
             <span>📧</span> {user.email}
           </p>}
           <p className="text-xs text-gray-400 flex items-center gap-2">
             <span>🛡️</span> Клан: <span className="text-white font-bold">{clanNames[user.clan_id] || 'Без клана'}</span>
           </p>
           {user.is_admin && (
             <p className="text-xs text-red-400 flex items-center gap-2 font-bold">
               <span>👑</span> Администратор проекта
             </p>
           )}
        </div>

        <button
          onClick={() => {
            navigate(`/profile/${user.id}`);
            onClose();
          }}
          className="w-full py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold text-sm transition flex items-center justify-center gap-2 shadow-lg shadow-blue-600/20"
        >
          👤 Открыть полный профиль
        </button>
      </div>
    </div>
  );
};

const MessagesPage: React.FC = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedUser, setSelectedUser] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [showMiniProfile, setShowMiniProfile] = useState<number | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTab, setPickerTab] = useState<PickerTab>('emoji');
  const [groupedStickers, setGroupedStickers] = useState<Record<number, PickerSticker[]>>({});
  const [stickerLoadError, setStickerLoadError] = useState<string | null>(null);

  const [uploadingFile, setUploadingFile] = useState(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerMedia, setViewerMedia] = useState<MediaItem[]>([]);

  // Групповые чаты
  const [groups, setGroups] = useState<any[]>([]);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<any>(null);

  // Пересылка
  const [forwardMessageId, setForwardMessageId] = useState<number | null>(null);

  // Контекстное меню
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; messageId: number; isOwn: boolean } | null>(null);

  // Редактирование сообщения
  const [editingMsgId, setEditingMsgId] = useState<number | null>(null);
  const [editMsgText, setEditMsgText] = useState('');

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const emojiPickerTriggerRef = useRef<HTMLButtonElement>(null);
  const stickerPickerTriggerRef = useRef<HTMLButtonElement>(null);
  const userId = localStorage.getItem('sharkly_user_id');
  const navigate = useNavigate();
  const call = useCall();

  const togglePicker = (tab: PickerTab) => {
    if (pickerOpen && pickerTab === tab) {
      setPickerOpen(false);
      return;
    }

    setPickerTab(tab);
    setPickerOpen(true);
  };

  useEffect(() => {
    fetchConversations();
    fetchMyStickers(); // Загружаем стикеры при старте
    const interval = setInterval(fetchConversations, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (selectedUser) {
      fetchMessages(selectedUser.user_id);
    }
  }, [selectedUser]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const res = await api.get('/messages/conversations');

      const uniqueConversations = res.data.reduce((acc: any[], curr: any) => {
        if (!acc.find(c => c.user_id === curr.user_id)) {
          const isMyMessage = curr.sender_id ? curr.sender_id.toString() === userId : false;
          acc.push({ ...curr, is_my_last_message: isMyMessage });
        }
        return acc;
      }, []);

      setConversations(uniqueConversations);
    } catch (err) {
      logHttpError("Ошибка загрузки диалогов", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (otherUserId: number) => {
    try {
      const res = await api.get(`/messages/${otherUserId}`);
      setMessages(res.data);
    } catch (err) {
      logHttpError("Ошибка загрузки сообщений", err);
    }
  };

 // Загрузка стикеров пользователя
const fetchMyStickers = async () => {
  setStickerLoadError(null);
  try {
    const res = await api.get('/stickers/my-stickers');

    // Сразу группируем стикеры по пакам для удобного отображения
    const grouped: Record<number, PickerSticker[]> = {};
    res.data.forEach((sticker: PickerSticker & { pack_id: number }) => {
      if (!grouped[sticker.pack_id]) {
        grouped[sticker.pack_id] = [];
      }
      grouped[sticker.pack_id].push(sticker);
    });
    setGroupedStickers(grouped);
  } catch (err) {
    setStickerLoadError('Не удалось загрузить стикеры');
    logHttpError("Ошибка загрузки стикеров", err);
  }
};

  // Групповые чаты
  const fetchGroups = async () => {
    try {
      const res = await api.get('/messages/group/list');
      setGroups(res.data);
    } catch {}
  };

  const selectGroup = async (group: any) => {
    setSelectedGroup(group);
    setSelectedUser(null);
    try {
      const res = await api.get(`/messages/group/${group.id}`);
      setMessages(res.data);
    } catch {}
  };

  const sendGroupMessage = async (file?: File) => {
    if ((!newMessage.trim() && !file) || !selectedGroup) return;
    try {
      let fileUrl = null;
      let fileType = null;
      if (file) {
        setUploadingFile(true);
        const formData = new FormData();
        formData.append('file', file);
        const uploadRes = await api.post('/messages/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        fileUrl = uploadRes.data.file_url;
        fileType = uploadRes.data.file_type;
        setUploadingFile(false);
      }
      await api.post(`/messages/group/${selectedGroup.id}/send`, {
        content: newMessage,
        file_url: fileUrl,
        file_type: fileType
      });
      setNewMessage('');
      setPreviewFile(null);
      const res = await api.get(`/messages/group/${selectedGroup.id}`);
      setMessages(res.data);
    } catch {
      setUploadingFile(false);
    }
  };

  useEffect(() => { fetchGroups(); }, []);

  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    if (query.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      const res = await api.get(`/messages/search/${encodeURIComponent(query)}`);
      setSearchResults(res.data);
    } catch (err) {
      logHttpError("Ошибка поиска", err);
    }
  };

  const startConversation = (user: SearchResult) => {
    const existingConv = conversations.find(c => c.user_id === user.id);
    if (existingConv) {
      setSelectedUser(existingConv);
    } else {
      setSelectedUser({
        user_id: user.id,
        username: user.username,
        avatar_url: user.avatar_url,
        last_message: '',
        last_message_time: new Date().toISOString(),
        unread_count: 0,
        is_my_last_message: false
      });
    }
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleDeleteMessage = async (msgId: number) => {
    try {
      await api.delete(`/messages/${msgId}`);
      setMessages(prev => prev.filter(m => m.id !== msgId));
    } catch {}
  };

  const handleEditMessage = async (msgId: number) => {
    if (!editMsgText.trim()) return;
    try {
      await api.put(`/messages/${msgId}`, { content: editMsgText });
      setMessages(prev => prev.map(m => m.id === msgId ? { ...m, content: editMsgText } : m));
      setEditingMsgId(null);
    } catch {}
  };

  // Отправка обычного сообщения или сообщения с файлом
  const handleSendMessage = async (file?: File) => {
    if (selectedGroup) { sendGroupMessage(file); return; }
    if ((!newMessage.trim() && !file) || !selectedUser) return;

    try {
      let fileUrl = null;
      let fileType = null;

      if (file) {
        setUploadingFile(true);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('receiver_id', selectedUser.user_id.toString());

        const uploadRes = await api.post('/messages/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        fileUrl = uploadRes.data.file_url;
        fileType = uploadRes.data.file_type;
        setUploadingFile(false);
      }

      await api.post('/messages/', {
        receiver_id: selectedUser.user_id,
        content: newMessage,
        file_url: fileUrl,
        file_type: fileType
      });

      setNewMessage('');
      setPreviewFile(null);
      await fetchMessages(selectedUser.user_id);
      setTimeout(() => fetchConversations(), 500);
    } catch (err) {
      logHttpError("Ошибка отправки", err);
      alert("Не удалось отправить сообщение");
      setUploadingFile(false);
    }
  };

  // Отправка только стикера
  const handleSendSticker = async (stickerId: number) => {
    if (!selectedUser) return;
    try {
      await api.post('/messages/', {
        receiver_id: selectedUser.user_id,
        content: '', // Стикеры обычно отправляются без текста
        sticker_id: stickerId
      });

      await fetchMessages(selectedUser.user_id);
      setTimeout(() => fetchConversations(), 500);
    } catch (err) {
      logHttpError("Ошибка отправки стикера", err);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPreviewFile(file);
    }
  };

  const insertEmoji = (emoji: string) => {
    setNewMessage(prev => prev + emoji);
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Только что';
    if (minutes < 60) return `${minutes} мин`;
    if (hours < 24) return `${hours} ч`;
    if (days < 7) return `${days} дн`;
    return date.toLocaleDateString('ru-RU');
  };

  const formatCallDuration = (message: Message) => {
    if (!message.call_connected_at || !message.call_ended_at) return null;
    const seconds = Math.max(0, Math.floor(
      (new Date(message.call_ended_at).getTime() - new Date(message.call_connected_at).getTime()) / 1000,
    ));
    const minutes = Math.floor(seconds / 60).toString().padStart(2, '0');
    return `${minutes}:${(seconds % 60).toString().padStart(2, '0')}`;
  };

  const callResult = (message: Message) => {
    if (message.call_state === 'missed') return 'Пропущенный звонок';
    if (message.call_state === 'declined') return 'Отклонённый звонок';
    if (message.call_state === 'failed' || message.call_state === 'interrupted') return 'Звонок не состоялся';
    const duration = formatCallDuration(message);
    return duration ? `Длительность ${duration}` : 'Звонок завершён';
  };

  const getAvatarUrl = (username: string, avatarUrl: string | null, size: number = 40, isSystem?: boolean) => {
    if (isSystem || username === "Избранное") {
       return `https://ui-avatars.com/api/?name=⭐&background=random&color=fff&size=${size}&rounded=true`;
    }
    if (avatarUrl) {
      return `${API_BASE}${avatarUrl}`;
    }
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(username)}&background=random&color=fff&size=${size}`;
  };

  const formatLastMessage = (conv: Conversation) => {
    if (!conv.last_message) return '';
    const prefix = conv.is_my_last_message ? 'Вы: ' : 'Вам: ';
    return `${prefix}${conv.last_message}`;
  };

  // Рендер содержимого сообщения (текст + стикер + медиа)
  const renderMessageContent = (msg: Message) => {
    if (msg.message_kind === 'call') {
      return (
        <div className="flex min-w-48 items-center gap-3 py-1" data-testid="call-history-message">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/10 text-xl">
            {msg.call_type === 'video' ? '🎥' : '📞'}
          </span>
          <div>
            <p className="font-semibold">{msg.call_type === 'video' ? 'Видеозвонок' : 'Аудиозвонок'}</p>
            <p className="text-xs opacity-75">{callResult(msg)}</p>
          </div>
        </div>
      );
    }
    return (
      <>
        {/* 1. Стикеры (отображаем первыми, если есть) */}
        {msg.sticker_url && (
          <div className="mb-1">
            <img
              src={`${API_BASE}${msg.sticker_url}`}
              alt="Sticker"
              className="w-32 h-auto max-w-[200px] drop-shadow-md"
            />
          </div>
        )}

        {/* 2. Текст */}
        {msg.content && editingMsgId === msg.id ? (
          <div className="mt-1">
            <textarea
              className="w-full bg-black/30 text-white text-sm rounded-lg p-2 border border-gray-600 focus:border-emerald-500 outline-none resize-none"
              rows={2}
              value={editMsgText}
              onChange={e => setEditMsgText(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleEditMessage(msg.id);
                }
                if (e.key === 'Escape') setEditingMsgId(null);
              }}
              autoFocus
            />
            <div className="flex gap-1 mt-1">
              <button onClick={() => handleEditMessage(msg.id)} className="text-xs text-emerald-400 hover:text-emerald-300">Сохранить</button>
              <button onClick={() => setEditingMsgId(null)} className="text-xs text-gray-400 hover:text-gray-300">Отмена</button>
            </div>
          </div>
        ) : msg.content && <p className="text-sm leading-relaxed whitespace-pre-wrap mb-1">{msg.content}</p>}

        {/* 3. Файлы (изображения, видео, документы) */}
        {msg.file_url && (
          <div className="mt-2 rounded-xl overflow-hidden max-w-sm">
            {msg.file_type === 'image' && (
              <img
                src={`${API_BASE}${msg.file_url}`}
                alt="Attachment"
                className="w-full h-auto cursor-pointer hover:opacity-90 transition"
                onClick={() => {
                  setViewerMedia([{
                    type: 'image' as const,
                    url: `${API_BASE}${msg.file_url}`
                  }]);
                  setViewerOpen(true);
                }}
              />
            )}
            {msg.file_type === 'video' && (
              <video
                src={`${API_BASE}${msg.file_url}`}
                controls
                className="w-full max-h-64 bg-black rounded-xl cursor-pointer"
                onClick={() => {
                  setViewerMedia([{
                    type: 'video' as const,
                    url: `${API_BASE}${msg.file_url}`
                  }]);
                  setViewerOpen(true);
                }}
              />
            )}
            {msg.file_type === 'file' && (
              <a
                href={`${API_BASE}${msg.file_url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 bg-black/20 rounded-xl hover:bg-black/30 transition text-sm"
              >
                <span className="text-2xl">📄</span>
                <span className="truncate flex-1">Скачать файл</span>
                <span className="text-xs opacity-70">⬇️</span>
              </a>
            )}
          </div>
        )}
      </>
    );
  };

  const stickerGroups: StickerGroup[] = Object.entries(groupedStickers).map(([packId, stickers]) => ({
    id: packId,
    name: stickers[0]?.pack_name ?? 'Стикеры',
    stickers,
  }));
  const hasSelectedChat = Boolean(selectedUser || selectedGroup);

  return (
    <div className="h-screen flex bg-[#0f1115] text-white overflow-hidden">
      {/* Список диалогов */}
      <div
        data-testid="conversation-list"
        className={`${hasSelectedChat ? 'hidden md:flex' : 'flex'} w-full md:w-80 bg-[#1a1d24] border-r border-gray-800 flex-col`}
      >
        <div className="p-4 border-b border-gray-800">
          <h2 className="text-xl font-bold mb-4">💬 Сообщения</h2>
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="🔍 Найти пользователя..."
              className="w-full bg-[#0f1115] border border-gray-700 rounded-full px-4 py-2 text-white text-sm focus:border-blue-500 outline-none"
            />
            {searchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-[#0f1115] border border-gray-700 rounded-xl shadow-xl z-10 max-h-64 overflow-y-auto">
                {searchResults.map((user) => (
                  <div
                    key={user.id}
                    onClick={() => startConversation(user)}
                    className="p-3 hover:bg-gray-800 cursor-pointer border-b border-gray-800 last:border-b-0 transition flex items-center gap-3"
                  >
                    <img src={getAvatarUrl(user.username, user.avatar_url, 32)} alt={user.username} className="w-8 h-8 rounded-full object-cover" />
                    <div>
                      <p className="font-bold text-sm text-white">{user.username}</p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Групповые чаты */}
        <div className="px-4 pt-3 pb-2 border-b border-gray-800">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">Группы</span>
            <button onClick={() => setShowCreateGroup(true)} className="text-blue-400 hover:text-blue-300 text-lg leading-none" title="Создать группу">+</button>
          </div>
          {groups.map(g => (
            <div
              key={g.id}
              onClick={() => selectGroup(g)}
              className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-gray-800/50 transition ${selectedGroup?.id === g.id ? 'bg-blue-600/20' : ''}`}
            >
              <div className="w-9 h-9 rounded-full bg-gray-700 flex items-center justify-center text-sm border border-gray-600">👥</div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-white truncate">{g.name}</p>
                <p className="text-xs text-gray-500 truncate">{g.member_count} участников</p>
              </div>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full"></div>
          </div>
        ) : conversations.length === 0 ? (
          <div className="flex-1 flex items-center justify-center p-4 text-center text-gray-500">
            <div>
              <p className="text-2xl mb-2">💬</p>
              <p className="text-sm">У вас пока нет диалогов</p>
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            {conversations.map((conv) => (
              <div
                key={conv.user_id}
                onClick={() => setSelectedUser(conv)}
                className={`p-4 border-b border-gray-800 cursor-pointer hover:bg-gray-800/50 transition flex items-center gap-3 ${
                  selectedUser?.user_id === conv.user_id ? 'bg-blue-600/20' : ''
                }`}
              >
                <img
                  src={getAvatarUrl(conv.username, conv.avatar_url, 48, conv.user_id === 0)}
                  alt={conv.username}
                  className="w-12 h-12 rounded-full object-cover border-2 border-gray-700 shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className={`font-bold truncate ${conv.user_id === 0 ? 'text-yellow-400' : 'text-white'}`}>
                        {conv.username}
                    </span>
                    {conv.unread_count > 0 && (
                      <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full shrink-0 ml-2">
                        {conv.unread_count}
                      </span>
                    )}
                  </div>
                  <p className={`text-sm truncate ${conv.user_id === 0 ? 'text-gray-500 italic' : (conv.unread_count > 0 ? 'text-white font-medium' : 'text-gray-400')}`}>
                    {conv.user_id === 0 ? 'Ваши заметки и файлы' : formatLastMessage(conv)}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">{formatTime(conv.last_message_time)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Окно чата */}
      <div
        data-testid="chat-pane"
        className={`${hasSelectedChat ? 'flex' : 'hidden'} md:flex flex-1 flex-col relative`}
      >
        {(selectedUser || selectedGroup) ? (
          <>
            {/* Заголовок чата */}
            <div className="p-4 border-b border-gray-800 bg-[#1a1d24] flex justify-between items-center relative z-20">
              <div className="flex items-center gap-3">
                 <button
                   type="button"
                   aria-label="Назад к диалогам"
                   onClick={() => {
                     setPickerOpen(false);
                     setSelectedUser(null);
                     setSelectedGroup(null);
                   }}
                   className="md:hidden rounded-full p-2 text-gray-400 transition hover:bg-gray-800 hover:text-white"
                 >
                   ←
                 </button>
                 {selectedGroup ? (
                    <div className="flex items-center gap-3 p-2">
                        <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center text-xl border border-gray-600">👥</div>
                        <div>
                            <h3 className="text-lg font-bold text-white">{selectedGroup.name}</h3>
                            <p className="text-xs text-gray-500">{selectedGroup.member_count} участников</p>
                        </div>
                    </div>
                 ) : selectedUser && selectedUser.user_id !== 0 ? (
                    <div className="relative">
                      <button
                        onClick={() => setShowMiniProfile(showMiniProfile === selectedUser!.user_id ? null : selectedUser!.user_id)}
                        className="flex items-center gap-3 hover:bg-gray-800/50 p-2 rounded-xl transition group"
                      >
                        <img
                          src={getAvatarUrl(selectedUser!.username, selectedUser!.avatar_url, 40)}
                          alt={selectedUser!.username}
                          className="w-10 h-10 rounded-full object-cover border-2 border-gray-700"
                        />
                        <div className="text-left">
                          <h3 className="text-lg font-bold text-white group-hover:text-blue-400 transition-colors">
                            {selectedUser!.username}
                          </h3>
                          <p className="text-xs text-gray-500">Нажми для профиля</p>
                        </div>
                      </button>

                      {showMiniProfile === selectedUser!.user_id && (
                        <MiniProfilePopup
                          userId={selectedUser!.user_id}
                          onClose={() => setShowMiniProfile(null)}
                        />
                      )}
                    </div>
                 ) : (
                    <div className="flex items-center gap-3 p-2">
                        <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center text-xl border border-yellow-500/50">⭐</div>
                        <div>
                            <h3 className="text-lg font-bold text-yellow-400">Избранное</h3>
                            <p className="text-xs text-gray-500">Личное пространство</p>
                        </div>
                    </div>
                 )}
              </div>

              {!selectedGroup && selectedUser && selectedUser.user_id !== 0 && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    aria-label={`Аудиозвонок пользователю ${selectedUser.username}`}
                    title="Аудиозвонок"
                    disabled={call.state.phase !== 'idle'}
                    onClick={() => void call.startCall(selectedUser.user_id, 'audio', {
                      name: selectedUser.username,
                      avatarUrl: selectedUser.avatar_url,
                    })}
                    className="rounded-full bg-gray-800 p-3 text-lg text-gray-200 transition hover:bg-blue-600 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    📞
                  </button>
                  <button
                    type="button"
                    aria-label={`Видеозвонок пользователю ${selectedUser.username}`}
                    title="Видеозвонок"
                    disabled={call.state.phase !== 'idle'}
                    onClick={() => void call.startCall(selectedUser.user_id, 'video', {
                      name: selectedUser.username,
                      avatarUrl: selectedUser.avatar_url,
                    })}
                    className="rounded-full bg-gray-800 p-3 text-lg text-gray-200 transition hover:bg-blue-600 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    🎥
                  </button>
                </div>
              )}

            </div>

            {/* Сообщения */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#0f1115]">
              {messages.length === 0 ? (
                <div className="text-center text-gray-500 mt-10">
                  <p className="text-4xl mb-2">{selectedGroup ? '👥' : selectedUser?.user_id === 0 ? '⭐' : '💬'}</p>
                  <p className="text-sm">
                    {selectedGroup
                      ? `Начните общение в "${selectedGroup.name}"`
                      : selectedUser?.user_id === 0
                        ? 'Здесь можно сохранять важные ссылки, файлы и мысли'
                        : `Начните общение с ${selectedUser?.username}`}
                  </p>
                </div>
              ) : (
                messages.map((msg) => {
                  const isMine = msg.sender_id.toString() === userId;
                  return (
                    <div key={msg.id} className={`flex flex-col ${isMine ? 'items-end' : 'items-start'} group`}>
                      <div className={`flex items-end gap-2 max-w-[70%] ${isMine ? 'flex-row-reverse' : ''}`}>
                        {!isMine && (
                          <img
                            src={getAvatarUrl(msg.sender_username, msg.sender_avatar_url, 32)}
                            alt={msg.sender_username}
                            className="w-8 h-8 rounded-full object-cover mb-1 border border-gray-700"
                          />
                        )}

                        <div
                          className={`px-4 py-2.5 rounded-2xl relative ${
                            isMine
                              ? 'bg-blue-600 text-white rounded-br-none'
                              : 'bg-gray-800 text-gray-200 rounded-bl-none border border-gray-700'
                          }`}
                          onContextMenu={msg.message_kind === 'call' ? undefined : (e) => {
                            e.preventDefault();
                            setContextMenu({ x: e.clientX, y: e.clientY, messageId: msg.id, isOwn: msg.sender_id.toString() === userId });
                          }}
                        >
                          {msg.forwarded_from_username && msg.forwarded_original_created_at && (
                            <p className={`mb-1 text-xs ${isMine ? 'text-blue-100' : 'text-gray-400'}`}>
                              Переслано от @{msg.forwarded_from_username} · {formatTime(msg.forwarded_original_created_at)}
                            </p>
                          )}
                          {renderMessageContent(msg)}

                          <div className={`flex items-center justify-end gap-1 mt-1 ${isMine ? 'text-blue-200' : 'text-gray-500'}`}>
                            <span className="text-[10px]">{formatTime(msg.created_at)}</span>
                            {isMine && (
                              <span className="text-xs font-bold">
                                {msg.is_read ? '✓✓' : '✓'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Реакции под сообщением */}
                      {msg.message_kind !== 'call' && (
                        <div className={`mt-1 ${isMine ? 'mr-2' : 'ml-10'}`}>
                          <ReactionPicker entityType="message" entityId={msg.id} align={isMine ? 'right' : 'left'} />
                        </div>
                      )}
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Предпросмотр файла перед отправкой */}
            {previewFile && (
              <div className="p-3 bg-[#1a1d24] border-t border-gray-800 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {previewFile.type.startsWith('image/') ? (
                    <img src={URL.createObjectURL(previewFile)} alt="Preview" className="w-12 h-12 rounded-lg object-cover" />
                  ) : previewFile.type.startsWith('video/') ? (
                    <span className="text-3xl">🎬</span>
                  ) : (
                    <span className="text-3xl">📄</span>
                  )}
                  <div>
                    <p className="text-sm font-bold truncate max-w-[200px]">{previewFile.name}</p>
                    <p className="text-xs text-gray-500">{(previewFile.size / 1024).toFixed(1)} KB</p>
                  </div>
                </div>
                <button
                  onClick={() => setPreviewFile(null)}
                  className="text-gray-400 hover:text-red-400 transition p-2"
                >
                  ✕
                </button>
              </div>
            )}

            {/* Поле ввода */}
            <div className="relative">
              <ChatMediaPicker
                isOpen={pickerOpen}
                activeTab={pickerTab}
                onTabChange={setPickerTab}
                onClose={() => setPickerOpen(false)}
                emojis={QUICK_EMOJIS}
                stickerGroups={stickerGroups}
                stickerError={stickerLoadError}
                outsideClickIgnoreRefs={[emojiPickerTriggerRef, stickerPickerTriggerRef]}
                returnFocusRef={pickerTab === 'emoji' ? emojiPickerTriggerRef : stickerPickerTriggerRef}
                onEmojiSelect={insertEmoji}
                onStickerSelect={(stickerId) => {
                  void handleSendSticker(stickerId);
                  setPickerOpen(false);
                }}
                onManageStickers={() => {
                  setPickerOpen(false);
                  navigate('/stickers');
                }}
              />
              <div className="p-4 border-t border-gray-800 bg-[#1a1d24]">
                <div className="flex gap-2 items-end">
                {/* Кнопки вложений */}
                <div className="flex gap-1 mb-1">
                  <button
                    ref={emojiPickerTriggerRef}
                    type="button"
                    onClick={() => togglePicker('emoji')}
                    aria-controls="chat-media-picker"
                    aria-expanded={pickerOpen && pickerTab === 'emoji'}
                    className={`p-3 rounded-full transition ${pickerOpen && pickerTab === 'emoji' ? 'bg-yellow-500/20 text-yellow-400' : 'hover:bg-gray-700 text-gray-400'}`}
                    title="Эмодзи"
                  >
                    😀
                  </button>
                  <button
                    ref={stickerPickerTriggerRef}
                    type="button"
                    onClick={() => togglePicker('sticker')}
                    aria-controls="chat-media-picker"
                    aria-expanded={pickerOpen && pickerTab === 'sticker'}
                    className={`p-3 rounded-full transition ${pickerOpen && pickerTab === 'sticker' ? 'bg-purple-500/20 text-purple-400' : 'hover:bg-gray-700 text-gray-400'}`}
                    title="Стикеры"
                  >
                    🎨
                  </button>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingFile}
                    className="p-3 rounded-full hover:bg-gray-700 text-gray-400 transition"
                    title="Прикрепить файл"
                  >
                    {uploadingFile ? '⏳' : '📎'}
                  </button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    accept="image/*,video/*,.pdf,.doc,.docx,.zip"
                    onChange={handleFileSelect}
                    disabled={uploadingFile}
                  />
                </div>

                {/* Текстовое поле */}
                <div className="flex-1 relative">
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage(previewFile || undefined);
                      }
                    }}
                    placeholder={selectedGroup ? "Написать в группу..." : selectedUser?.user_id === 0 ? "Сохранить заметку или файл..." : "Написать сообщение..."}
                    rows={1}
                    className="w-full bg-[#0f1115] border border-gray-700 rounded-2xl px-4 py-3 pr-12 text-white focus:border-blue-500 outline-none resize-none max-h-32 overflow-y-auto"
                    style={{ minHeight: '48px' }}
                  />
                </div>

                {/* Кнопка отправки */}
                <button
                  onClick={() => handleSendMessage(previewFile || undefined)}
                  disabled={(!newMessage.trim() && !previewFile) || uploadingFile}
                  className={`p-3 rounded-full font-bold transition mb-1 ${
                    (newMessage.trim() || previewFile) && !uploadingFile
                      ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_15px_rgba(37,99,235,0.4)]'
                      : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {uploadingFile ? '⏳' : '➤'}
                </button>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-500 bg-[#0f1115]">
            <div className="text-center">
              <p className="text-6xl mb-4 opacity-50">💬</p>
              <p className="text-lg font-medium">Выберите диалог слева</p>
            </div>
          </div>
        )}
      {/* Media Viewer */}
      <MediaViewer isOpen={viewerOpen} onClose={() => setViewerOpen(false)} media={viewerMedia} />

      {/* Контекстное меню */}
      {contextMenu && (
        <MessageContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          isOwn={contextMenu.isOwn}
          onForward={() => {
            setForwardMessageId(contextMenu.messageId);
            setContextMenu(null);
          }}
          onEdit={() => {
            const msg = messages.find(m => m.id === contextMenu.messageId);
            if (msg) {
              setEditingMsgId(contextMenu.messageId);
              setEditMsgText(msg.content || '');
            }
            setContextMenu(null);
          }}
          onDelete={() => {
            handleDeleteMessage(contextMenu.messageId);
            setContextMenu(null);
          }}
          onClose={() => setContextMenu(null)}
        />
      )}

      {/* Модалка пересылки */}
      {forwardMessageId && (
        <ForwardModal messageId={forwardMessageId} onClose={() => setForwardMessageId(null)} />
      )}

      {/* Модалка создания группы */}
      {showCreateGroup && (
        <CreateGroupModal onClose={() => setShowCreateGroup(false)} onCreated={() => { setShowCreateGroup(false); fetchGroups(); }} />
      )}
      </div>
    </div>
  );
};

export default MessagesPage;
